// Modal for retrying verification with updated credentials
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axios from 'axios';
import API from '../config';

export default function RetryVerificationModal({ open, onClose, onSuccess, token, errorMessage }) {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.email || !credentials.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API}/api/subscriptions/refresh_subscriptions`,
        credentials,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Success - call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
      
      // Close modal
      setCredentials({ email: '', password: '' });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to retry verification');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setCredentials({ email: '', password: '' });
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          m: { xs: 0, sm: 2 },
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.error.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <RefreshIcon 
            sx={{ 
              fontSize: 32, 
              color: theme.palette.error.main 
            }} 
          />
        </Box>
        
        <Typography 
          variant="h5" 
          fontWeight={700} 
          gutterBottom
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Retry Verification
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' }, px: { xs: 1, sm: 0 } }}
        >
          Update your OnlyFans credentials to retry verification
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 4 }, pt: 2, pb: 1 }}>
        <Box component="form" onSubmit={handleSubmit}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Previous Error:
              </Typography>
              {errorMessage}
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 3 }}>
            Enter your OnlyFans credentials. We'll verify your account and load your subscriptions.
          </Alert>

          <TextField
            fullWidth
            label="OnlyFans Email"
            type="email"
            value={credentials.email}
            onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
            required
            sx={{ mb: 2 }}
            disabled={loading}
          />

          <TextField
            fullWidth
            label="OnlyFans Password"
            type={showPassword ? 'text' : 'password'}
            value={credentials.password}
            onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
            required
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Typography 
            variant="caption" 
            color="text.secondary" 
            display="block" 
            sx={{ mt: 1 }}
          >
            Your password is encrypted and only used to verify your subscriptions.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: { xs: 2, sm: 4 }, 
          pb: { xs: 3, sm: 4 }, 
          pt: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={loading}
          fullWidth={isMobile}
          sx={{ borderRadius: 2, order: { xs: 2, sm: 1 } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !credentials.email || !credentials.password}
          fullWidth={isMobile}
          sx={{
            px: 4,
            borderRadius: 2,
            order: { xs: 1, sm: 2 },
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
              Retrying...
            </>
          ) : (
            'Retry Verification'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

